import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";
import { useEverifyUserProfile } from "../hooks/everify/useEverifyUserProfile";
import { useGetEverifyCompanies } from "../hooks/superAdminEverify/useGetEverifyCompanies";
import { deriveEverifyAccess } from "../lib/everifyRoles";
import { getActiveCompanyId, setActiveCompanyId } from "../lib/companyContext";

const CompanySelectionContext = createContext(null);

// When nothing is already selected, default to this company if it's in the
// user's available list — product decision, not a fallback/error value.
const PREFERRED_DEFAULT_COMPANY_ID = 69;

export const CompanySelectionProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [selectedCompanyId, setSelectedCompanyId] = useState(() => getActiveCompanyId());

  const profileQuery = useEverifyUserProfile(isAuthenticated);
  const access = useMemo(() => deriveEverifyAccess(profileQuery.data), [profileQuery.data]);

  const companiesQuery = useGetEverifyCompanies({
    enabled: isAuthenticated && access.tier === "superadmin",
    // Bootstrap call: no company is selected yet (that's what this call is
    // for), so there's nothing real to scope it to. The backend requires the
    // header to be present regardless of role, so send a placeholder — it
    // doesn't affect the result for a SuperAdmin-authorized token.
    headers: { "x-company-id": "1" },
  });

  const companies = useMemo(() => {
    if (access.tier === "superadmin") {
      return (companiesQuery.data || []).map((company) => ({
        companyId: Number(company.id ?? company.companyId),
        name: company.name || company.companyName || `Company ${company.id}`,
      }));
    }
    return access.companies;
  }, [access, companiesQuery.data]);

  // Reset when signed out, and pick a valid default whenever the resolved
  // company list changes (login, tier resolved, or company list loaded).
  useEffect(() => {
    if (!isAuthenticated) {
      setSelectedCompanyId(null);
      return;
    }
    if (!companies.length) return;

    const stored = getActiveCompanyId();
    const storedIsValid = companies.some((c) => c.companyId === stored);
    const preferredDefault = companies.find(
      (c) => c.companyId === PREFERRED_DEFAULT_COMPANY_ID
    );
    const nextId = storedIsValid
      ? stored
      : (preferredDefault || companies[0]).companyId;

    if (nextId !== selectedCompanyId) {
      setSelectedCompanyId(nextId);
    }
    if (nextId !== stored) {
      setActiveCompanyId(nextId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, companies]);

  const selectCompany = (companyId) => {
    const id = Number(companyId);
    if (!Number.isFinite(id) || id <= 0) return;
    setActiveCompanyId(id);
    setSelectedCompanyId(id);
    // Every company-scoped query result depends on x-company-id — refetch
    // once the active company changes instead of showing stale data.
    queryClient.invalidateQueries();
  };

  const isResolvingAccess = isAuthenticated && (profileQuery.isLoading || profileQuery.isPending);
  const isResolvingCompanies =
    isAuthenticated && access.tier === "superadmin" && companiesQuery.isLoading;
  const accessResolved = !isResolvingAccess && !isResolvingCompanies;
  // Don't block forever if resolution finished but there's genuinely nothing
  // to select (no Everify access, or a malformed/empty role list).
  const hasSelection =
    !isAuthenticated ||
    Boolean(selectedCompanyId) ||
    access.tier === "none" ||
    (accessResolved && companies.length === 0);

  const value = useMemo(
    () => ({
      tier: access.tier,
      companies,
      selectedCompanyId,
      selectedCompany: companies.find((c) => c.companyId === selectedCompanyId) || null,
      selectCompany,
      loading: isResolvingAccess || isResolvingCompanies || !hasSelection,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [access.tier, companies, selectedCompanyId, isResolvingAccess, isResolvingCompanies, hasSelection]
  );

  return (
    <CompanySelectionContext.Provider value={value}>
      {children}
    </CompanySelectionContext.Provider>
  );
};

export const useCompanySelection = () => useContext(CompanySelectionContext);
